import { IBackendCartEntries, ILegacyCartEntry } from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import { CartEntryType } from 'utils/cart/types';

// Not every cartEntry can be mapped. return null for an un-mappable entry
const cartEntryTypesMap = {
  [CartEntryType.combo]: MenuObjectTypes.COMBO,
  [CartEntryType.comboSlot]: MenuObjectTypes.COMBO_SLOT,
  [CartEntryType.item]: MenuObjectTypes.ITEM,
  [CartEntryType.itemOption]: MenuObjectTypes.ITEM_OPTION,
  [CartEntryType.itemOptionModifier]: MenuObjectTypes.ITEM_OPTION_MODIFIER,
  [CartEntryType.offerDiscount]: null,
  [CartEntryType.offerCombo]: MenuObjectTypes.COMBO,
  [CartEntryType.offerItem]: null,
};

export const maybeMapCartEntryToMenuObjectIdentifier = (
  entry: ILegacyCartEntry | IBackendCartEntries
) => {
  const entryType = cartEntryTypesMap[entry.type as keyof typeof cartEntryTypesMap];
  const sanityId = entry?.sanityId || ('_id' in entry ? entry._id : '');
  const isMappable = !!(entryType && sanityId);
  return isMappable ? `${entryType}-${sanityId}` : null;
};

export const maybeMapCartEntryToEntryTypeAndId = (
  entry: ILegacyCartEntry | IBackendCartEntries
) => {
  const menuObjectType = cartEntryTypesMap[entry.type as keyof typeof cartEntryTypesMap];
  const sanityId = entry?.sanityId || ('_id' in entry ? entry._id : '');
  return { menuObjectType, sanityId };
};

/**
 * An item with an applied offer/reward has its `type` reassigned to `offerItem` (see
 * `state/cart/helpers.ts`'s `mapEntryTypeToOfferType`), which `cartEntryTypesMap` treats as
 * unmappable (`null`) since an offer can also wrap a combo/discount. It is still backed by the
 * same underlying `item` menu object, so callers that need a fresh menu-object lookup for an
 * offer-benefit item (e.g. re-localizing its display name) should resolve against this
 * underlying type instead of the offer-reassigned one — otherwise the entry is permanently
 * unmappable and the lookup is always skipped. Scoped to a dedicated helper (rather than baked
 * into the two lookups above) so it does not change behavior for existing callers — like
 * availability checks — that were not written to expect it.
 */
export const maybeMapCartEntryToEntryTypeAndIdForDisplay = (
  entry: ILegacyCartEntry | IBackendCartEntries
) =>
  maybeMapCartEntryToEntryTypeAndId(
    entry.type === CartEntryType.offerItem ? { ...entry, type: CartEntryType.item } : entry
  );
